<template>
  <section class="list-filters">
    <Dropdown
      v-if="(currentDevice.isMobile || currentDevice.isTablet) && sortOptions"
      :title="i18n.orderBy.title"
      :subtitle="i18n.orderBy.options[currentSort.name]"
      details-class="list-filters__filter list-filter"
      summary-class="list-filter__summary"
      content-class="list-filter__content"
    >
      <template #content>
        <div
          v-for="option in sortOptions"
          :key="option.name"
          class="list-filter__option"
          @click="changeSort(option.field, option.direction)"
        >
          <input
            :id="`sort-${option.name}`"
            :value="`${option.field} - ${option.direction}`"
            name="sort"
            type="radio"
            class="list-filter__radio"
            :checked="option.field === sort.field && option.direction === sort.direction"
          />
          <label :for="`option-${option.name}`" class="list-filter__label">
            {{ i18n.orderBy.options[option.name] }}
          </label>
        </div>
      </template>
    </Dropdown>

    <Dropdown
      v-if="props.filters.offers && props.filters.offers.visible"
      :title="filtersComputed.offers.title"
      :subtitle="filtersComputed.offers.legend"
      details-class="list-filters__filter list-filter"
      summary-class="list-filter__summary"
      content-class="list-filter__content"
    >
      <template #content>
        <span class="dropdown__legend t-label">{{ filtersComputed.offers.legend }}</span>
        <div v-for="offer in filtersComputed.offers.options" :key="offer.id" class="list-filter__option">
          <input
            :id="`offer-${offer.id}`"
            :checked="isFilterSelected('offers', offer.value)"
            type="checkbox"
            class="list-filter__checkbox"
            :disabled="!offer.count"
            @change="toggleFilter('offers', offer.value, $event.target.checked)"
            @click="UtagEventFilterOfferClick(offer)"
          />
          <label
            :for="`offer-${offer.id}`"
            class="list-filter__label utag-list-filter__label"
            :class="{ disabled: !offer.count }"
          >
            {{ offer.label }} ({{ offer.count }})
          </label>
        </div>
      </template>
    </Dropdown>

    <Dropdown
      :title="filtersComputed.destinations.title"
      :subtitle="filtersComputed.destinations.legend"
      details-class="list-filters__filter list-filter"
      summary-class="list-filter__summary"
      content-class="list-filter__content"
    >
      <template #content>
        <span class="dropdown__legend t-label">{{ filtersComputed.destinations.legend }}</span>
        <template v-for="destination in filtersComputed.destinations.options" :key="destination.id">
          <div
            v-if="!destination.value.children || destination.value.children.length === 0"
            class="list-filter__option"
          >
            <input
              :id="`destination-${destination.id}`"
              :checked="isFilterSelected('destinations', destination.value)"
              type="checkbox"
              class="list-filter__checkbox"
              :disabled="!destination.count"
              :data-test="`list-filters__destination-${destination.id}`"
              @change="toggleFilter('destinations', destination.value, $event.target.checked)"
              @click="handleFilterClick('countries', destination.label)"
            />
            <label
              :for="`destination-${destination.id}`"
              class="list-filter__label"
              :class="{ disabled: !destination.count }"
            >
              {{ destination.label }} ({{ destination.count }})
            </label>
          </div>
          <Dropdown v-else class="list-filter__destination" content-class="list-filter__subdestinations">
            <template #header>
              <div class="list-filter__option">
                <input
                  :id="`destination-${destination.id}`"
                  type="checkbox"
                  class="list-filter__checkbox"
                  :class="getCheckedSubdestinationsClass(destination)"
                  :disabled="!destination.count"
                  :data-test="`list-filters__destination-${destination.id}`"
                  @click="e => handleDestinationClick(destination, e)"
                />
                <label
                  :for="`destination-${destination.id}`"
                  class="list-filter__label"
                  :class="{ disabled: !destination.count }"
                >
                  {{ destination.label }} ({{ destination.count }})
                </label>
              </div>
            </template>
            <template #content>
              <div v-for="child in destination.value.children" :key="child.id" class="list-filter__option-container">
                <div class="list-filter__option">
                  <input
                    :id="`destination-${child.id}`"
                    :checked="isFilterSelected('destinations', child.value)"
                    type="checkbox"
                    class="list-filter__checkbox"
                    :disabled="!child.count"
                    @change="toggleFilter('destinations', child.value, $event.target.checked)"
                    @click="handleFilterClick('countries', child.label)"
                  />
                  <label
                    :for="`destination-${child.id}`"
                    class="list-filter__label"
                    :class="{ disabled: !child.count }"
                  >
                    {{ child.label }} ({{ child.count }})
                  </label>
                </div>
                <div v-if="child.count > 0" class="list-filter__action">
                  <span class="list-filter__only-button" @click="selectOnlyDestinationFilter(child.value)">
                    {{ i18n.filters.only }}
                  </span>
                </div>
              </div>
            </template>
          </Dropdown>
        </template>
      </template>
    </Dropdown>

    <Dropdown
      v-if="props.filters.dates && props.filters.dates.visible"
      :title="filtersComputed.dates.title"
      :subtitle="filtersComputed.dates.legend"
      details-class="list-filters__filter list-filter"
      summary-class="list-filter__summary"
      content-class="list-filter__content"
    >
      <template #content>
        <!-- 888140 - Cambio copies en filtros -> Iberostar wanted to remove the dates filter legend -->
        <!-- <span class="dropdown__legend t-label">{{ filtersComputed.dates.legend }}</span> -->
        <div v-for="date in filtersComputed.dates.options" :key="date.id" class="list-filter__option">
          <input
            :id="`date-${date.id}`"
            :checked="isFilterSelected('dates', date.value)"
            type="checkbox"
            class="list-filter__checkbox"
            :disabled="!date.count"
            @change="toggleFilter('dates', date.value, $event.target.checked)"
            @click="handleFilterClick('countries', date.label)"
          />
          <label :for="`date-${date.id}`" class="list-filter__label" :class="{ disabled: !date.count }">
            {{ date.label }}( {{ date.count }})
          </label>
        </div>
      </template>
    </Dropdown>

    <Dropdown
      :title="i18n.filters.servicesTitle"
      :subtitle="`${filtersComputed.categories.legend}, ${filtersComputed.overview_features.legend}`"
      details-class="list-filters__filter list-filter"
      summary-class="list-filter__summary"
      content-class="list-filter__content"
    >
      <template #content>
        <span class="dropdown__legend t-label">{{ filtersComputed.categories.legend }}</span>
        <div v-for="category in filtersComputed.categories.options" :key="category.id" class="list-filter__option">
          <input
            :id="`category-${category.id}`"
            :checked="isFilterSelected('categories', category.value)"
            type="checkbox"
            class="list-filter__checkbox"
            :disabled="!category.count"
            @change="toggleFilter('categories', category.value, $event.target.checked)"
            @click="handleFilterClick('stars', category.label)"
          />
          <label
            :for="`category-${category.id}`"
            class="list-filter__label"
            :class="{ disabled: !category.count }"
            :data-test="`list-filters__category-${category.id}`"
          >
            <span :class="`rating-stars r${category.label}`"></span>
            ({{ category.count }})
          </label>
        </div>

        <span class="mt-2 dropdown__legend t-label">{{ filtersComputed.overview_features.legend }}</span>
        <div
          v-for="overview_feature in filtersComputed.overview_features.options"
          :key="overview_feature.id"
          class="list-filter__option"
        >
          <input
            :id="`overview-feature-${overview_feature.id}`"
            :checked="isFilterSelected('overview_features', overview_feature.value)"
            type="checkbox"
            class="list-filter__checkbox"
            :disabled="!overview_feature.count"
            @change="toggleFilter('overview_features', overview_feature.value, $event.target.checked)"
            @click="handleFilterClick('services', overview_feature.value?.title_en || overview_feature.label)"
          />
          <label
            :for="`overview-feature-${overview_feature.id}`"
            class="list-filter__label"
            :class="{ disabled: !overview_feature.count }"
          >
            {{ overview_feature.label }}
            ({{ overview_feature.count }})
            <tippy
              v-if="overview_feature.value.tooltip"
              :content="overview_feature.value.tooltip"
              theme="light"
              trigger="focus mouseenter click"
              :placement="tooltipPlacement"
              :z-index="tooltipZIndex"
            >
              <span class="list-filter__tooltip s s-alert-info"></span>
            </tippy>
          </label>
        </div>
      </template>
    </Dropdown>

    <Dropdown
      :title="filtersComputed.specialties.title"
      :subtitle="filtersComputed.specialties.legend"
      details-class="list-filters__filter list-filter"
      summary-class="list-filter__summary"
      content-class="list-filter__content"
    >
      <template #content>
        <span class="dropdown__legend t-label">{{ filtersComputed.specialties.legend }}</span>
        <div v-for="specialty in filtersComputed.specialties.options" :key="specialty.id" class="list-filter__option">
          <input
            :id="`specialty-${specialty.id}`"
            :checked="isFilterSelected('specialties', specialty.value)"
            type="checkbox"
            class="list-filter__checkbox"
            :disabled="!specialty.count"
            @change="toggleFilter('specialties', specialty.value, $event.target.checked)"
            @click="handleFilterClick('specialities', specialty.value?.title_en || specialty.label)"
          />
          <label :for="`specialty-${specialty.id}`" class="list-filter__label" :class="{ disabled: !specialty.count }">
            {{ specialty.label }} ({{ specialty.count }})
          </label>
        </div>
      </template>
    </Dropdown>
  </section>
</template>

<script setup>
import { storeToRefs } from 'pinia'
import { computed, inject, onMounted, watch } from 'vue'
import { Tippy } from 'vue-tippy'

import { currentDevice } from '../../../../../core/utils/currentDevice'
import { useHotelsInfoStore } from '../../../blocks/HotelsInfo/store/hotels_info'
import { loadStyles } from '../../../mixins/LoadStyles/LoadStyles'
import Dropdown from '../../Dropdown/Dropdown'
import { emitUtagEvent } from '../HotelsInfo.analytics'
import sortOptions from '../ListSort/sortOptions'

/**
 * Styles
 */
const COMPONENT_NAME = 'ListFilters'
loadStyles({ name: COMPONENT_NAME, folder: 'components/HotelsInfo' })

/**
 * Inject
 */
const i18n = inject('i18n')

/**
 * Props
 */
const props = defineProps({
  filters: {
    type: Object,
    required: true,
    default: () => ({}),
  },
  mobileFiltersState: {
    type: Object,
    default: null, // Será null en desktop, un objeto en mobile
  },
})

/**
 * Store
 */
const hotelsInfoStore = useHotelsInfoStore()
const {
  filters: selectedFiltersFromStore,
  hotelsFiltered: hotels,
  hotelsFilteredToOffers,
  hotelsFilteredToDestinations,
} = storeToRefs(hotelsInfoStore)

if (props.filters.offers && props.filters.offers.visible) {
  hotelsInfoStore.setOffers(props.filters.offers.options.map(offer => offer.value))
}

const { sort } = storeToRefs(hotelsInfoStore)
hotelsInfoStore.setSort(sortOptions[0].field, sortOptions[0].direction)

/**
 * Emits
 */
const emit = defineEmits(['update:mobileFiltersState'])

/**
 * Computed
 */
const isMobile = computed(() => currentDevice.isMobile)
const tooltipPlacement = computed(() => (isMobile.value ? 'bottom' : 'right'))
const tooltipZIndex = computed(() => (isMobile.value ? 999999 : 999))

const currentFilters = computed(() => {
  return (currentDevice.isMobile || currentDevice.isTablet) && props.mobileFiltersState
    ? props.mobileFiltersState
    : selectedFiltersFromStore.value
})

const filtersComputed = computed(() => {
  const filtersAux = props.filters

  // Filter offers
  const hotelIds = hotelsFilteredToOffers.value.reduce((acc, hotel) => {
    acc.push(hotel.id)
    return acc
  }, [])
  filtersAux.offers?.options?.map(offer => {
    offer.count = offer.value.hotel_ids.filter(id => hotelIds.includes(id)).length
    return offer
  })

  // Filter destinations
  filtersAux.destinations.options.map(destination => {
    destination.count = hotelsFilteredToDestinations.value.filter(hotel =>
      destination.value.hotels.includes(hotel.id)
    ).length

    destination.value.children.map(child => {
      child.count = hotelsFilteredToDestinations.value.filter(hotel => child.value.hotels.includes(hotel.id)).length
      return child
    })

    return destination
  })

  // Filter dates
  filtersAux.dates?.options?.map(date => {
    date.count = hotels.value.filter(hotel => date.value.hotels.includes(hotel.id)).length
    return date
  })

  // Filter categories
  filtersAux.categories.options.map(category => {
    category.count = hotels.value.filter(hotel => hotel.hotel_category === category.id).length
    return category
  })

  // Filter overview features
  filtersAux.overview_features.options.map(overview_feature => {
    overview_feature.count = hotels.value.filter(hotel =>
      hotel.overview_features_ids?.includes(overview_feature.id)
    ).length
    return overview_feature
  })

  // Filter specialties
  filtersAux.specialties.options.map(specialty => {
    specialty.count = hotels.value.filter(hotel => hotel.hotel_specialty_ids.includes(specialty.id)).length
    return specialty
  })

  return filtersAux
})

const currentSort = computed(() => {
  return sortOptions.find(option => option.field === sort.value.field && option.direction === sort.value.direction)
})

/**
 * Methods
 */

const isFilterSelected = (filterKey, filterValue) => {
  if (!currentFilters.value[filterKey]) {
    return false
  }
  if (filterValue && typeof filterValue === 'object' && filterValue.id !== undefined) {
    return currentFilters.value[filterKey].some(item => item.id === filterValue.id)
  }
  return currentFilters.value[filterKey].includes(filterValue)
}

const toggleFilter = (filterKey, filterValue, isChecked) => {
  if (currentDevice.isMobile || currentDevice.isTablet) {
    const newFiltersState = JSON.parse(JSON.stringify(currentFilters.value))
    let currentFilterArray = newFiltersState[filterKey] || []

    if (isChecked) {
      if (filterValue && typeof filterValue === 'object' && filterValue.id !== undefined) {
        if (!currentFilterArray.some(item => item.id === filterValue.id)) {
          currentFilterArray.push(filterValue)
        }
      } else if (!currentFilterArray.includes(filterValue)) {
        currentFilterArray.push(filterValue)
      }
    } else if (filterValue && typeof filterValue === 'object' && filterValue.id !== undefined) {
      currentFilterArray = currentFilterArray.filter(item => item.id !== filterValue.id)
    } else {
      currentFilterArray = currentFilterArray.filter(item => item !== filterValue)
    }
    newFiltersState[filterKey] = currentFilterArray
    emit('update:mobileFiltersState', newFiltersState)
  } else if (isChecked) {
    hotelsInfoStore.updateFilters(filterKey, [filterValue])
  } else {
    hotelsInfoStore.removeFilters(filterKey, filterValue)
  }
}
const updateFiltersWithParams = () => {
  // Check url params and update filters
  const urlParams = new URLSearchParams(window.location.search)
  if (urlParams.has('filter_offers')) {
    const offerIds = urlParams.get('filter_offers').split(',').map(Number)
    const offers = offerIds.map(offer => {
      const option = props.filters.offers.options.find(option => option.id === offer)
      return option.value
    })
    hotelsInfoStore.updateFilters('offers', offers)
  }
  if (urlParams.has('filter_destinations') || urlParams.has('destination')) {
    const destinationKey = urlParams.has('filter_destinations') ? 'filter_destinations' : 'destination'
    const destinationIds = urlParams.get(destinationKey).split(',').map(Number)
    const destinations = destinationIds
      .map(destination => {
        let option = props.filters.destinations.options.find(option => option.id === destination)

        if (!option) {
          props.filters.destinations.options.some(dest => {
            option = dest.value.children.find(child => child.id === destination)
            return option
          })
        }

        if (!option) {
          return false
        }

        if (option.value.children && option.value.children.length > 0) {
          return option.value.children.map(child => child.value)
        }

        return option.value
      })
      .filter(Boolean)
      .flat()

    hotelsInfoStore.updateFilters('destinations', destinations)
  }
  if (urlParams.has('filter_categories')) {
    const categoryIds = urlParams.get('filter_categories').split(',').map(String)
    const categories = categoryIds.map(category => {
      const option = props.filters.categories.options.find(option => option.id === category)
      return option.value
    })
    hotelsInfoStore.updateFilters('categories', categories)
  }
  if (urlParams.has('filter_specialties')) {
    const specialtyIds = urlParams.get('filter_specialties').split(',').map(Number)
    const specialties = specialtyIds.map(specialty => {
      const option = props.filters.specialties.options.find(option => option.id === specialty)
      return option.value
    })
    hotelsInfoStore.updateFilters('specialties', specialties)
  }
}

const changeSort = (field, direction) => {
  hotelsInfoStore.setSort(field, direction)
  emitUtagEvent({
    event_name: 'sort_by',
    event_cat: 'destinations',
    event_act: 'sort by',
    event_lbl: field,
    event_purpose: 'inspiration',
    event_structure: 'body',
  })
}
const UtagEventFilterOfferClick = offer => {
  emitUtagEvent({
    event_name: 'filters',
    event_cat: 'filters destinations',
    event_act: 'offers',
    event_lbl: offer.value.title_en,
    event_structure: 'body',
    event_purpose: 'inspiration',
  })
}
const getCheckedSubdestinationsClass = destination => {
  if (!currentFilters.value.destinations) {
    return false
  }

  if (destination.value.children.every(child => isFilterSelected('destinations', child.value))) {
    return 'list-filter__checkbox--checked'
  }
  if (destination.value.children.some(child => isFilterSelected('destinations', child.value))) {
    return 'list-filter__checkbox--partial'
  }
  return false
}

const handleFilterClick = (act, label) => {
  emitUtagEvent({
    event_name: 'filters',
    event_cat: 'filters destinations',
    event_act: act,
    event_lbl: label,
    event_purpose: 'inspiration',
    event_structure: 'body',
  })
}

const handleDestinationClick = (destination, event) => {
  // Avoid input being checked automatically

  event && event.preventDefault()

  if (currentDevice.isMobile || currentDevice.isTablet) {
    const newFiltersState = JSON.parse(JSON.stringify(currentFilters.value))
    let currentDestinations = newFiltersState.destinations || []

    if (!getCheckedSubdestinationsClass(destination)) {
      destination.value.children.forEach(child => {
        if (!currentDestinations.some(item => item.id === child.value.id)) {
          currentDestinations.push(child.value)
        }
      })
    } else {
      destination.value.children.forEach(child => {
        currentDestinations = currentDestinations.filter(item => item.id !== child.value.id)
      })
    }
    newFiltersState.destinations = currentDestinations
    emit('update:mobileFiltersState', newFiltersState)
  } else if (!getCheckedSubdestinationsClass(destination)) {
    hotelsInfoStore.updateFilters(
      'destinations',
      destination.value.children.map(child => child.value)
    )
  } else {
    destination.value.children.forEach(child => {
      hotelsInfoStore.removeFilters('destinations', child.value)
    })
  }

  handleFilterClick('countries', destination.label)
}

const selectOnlyDestinationFilter = value => {
  if (currentDevice.isMobile || currentDevice.isTablet) {
    const newFiltersState = JSON.parse(JSON.stringify(currentFilters.value))
    newFiltersState.destinations = [value]
    emit('update:mobileFiltersState', newFiltersState)
  } else {
    hotelsInfoStore.replaceFilters('destinations', [value])
  }
}

/**
 * Lifecycle
 */
onMounted(() => {
  updateFiltersWithParams()
})

watch(
  () => props.filters,
  () => {
    updateFiltersWithParams()
  }
)
</script>
